import {
  toPlanAdjustment,
  toTargetAdvanced,
} from '@flowx/designer/modules/ai-builder/types/conversation.types'
import {
  HarnessFrame,
  toBranchHandoff,
  toInterrupt,
  toSessionRecord,
} from '@flowx/designer/modules/ai-builder/types/harness.types'
import {
  asBoolean,
  asNonBlankString,
  asNumber,
  asString,
  asStringList,
  isRecord,
} from '@flowx/designer/modules/ai-builder/utils/payload.util'

/**
 * One wire event to a typed frame, or `null` to skip it.
 *
 * Shared by the live stream (`harness.api.ts`) and the replay of a session's saved history
 * (`harness-session.reducer.ts`): the harness keeps its public history as the same `{event, data}`
 * pairs it streamed, so a reload and a live turn go through one reading of the vocabulary.
 *
 * Unparseable and unrecognised frames are dropped rather than thrown, as `ConversationApiService`
 * does it: erroring here would tear down the subscription and lose every later frame over one bad
 * one. A dropped frame costs a line of the transcript; a dropped stream costs the turn.
 */
export function toFrame(event: string, raw: string): HarnessFrame | null {
  const data = parseJson(raw)

  if (!data) {
    console.warn('[harness] dropped an unparseable frame', { event, raw })

    return null
  }

  return frameFromEvent(event, data)
}

export function frameFromEvent(event: string, data: Record<string, unknown>): HarnessFrame | null {
  switch (event) {
    case 'story_started':
    case 'story_implemented': {
      const eventId = asNonBlankString(data['event_id'])
      const runId = asNonBlankString(data['run_id'])
      const storyId = asNonBlankString(data['story_id'])
      const name = asNonBlankString(data['name'])

      return eventId && runId && storyId && name
        ? {
            type: event,
            eventId,
            runId,
            storyId,
            name,
            commitId: event === 'story_implemented' ? asNonBlankString(data['commit_id']) : null,
          }
        : null
    }

    case 'token':
      return { type: 'token', text: asString(data['text']) }

    case 'tool_use_start':
      return {
        type: 'tool_use_start',
        ...readToolFields(data),
        arguments: data['arguments'],
      }

    case 'tool_use_result':
      return {
        type: 'tool_use_result',
        ...readToolFields(data),
        content: data['content'],
      }

    // Never emitted by today's harness, so this is untested tolerance. `content` is read the way
    // `tool_use_result` reads it, rather than passing the whole interrupt through.
    case 'finding_result':
      return { type: 'finding_result', content: data['content'] ?? data }

    // The three interrupt events carry the same shape. `type` distinguishes them and says more
    // than the event name: `fix_review` and `ship_review` both arrive as `interrupt`.
    case 'ask_user':
    case 'plan_proposed':
    case 'interrupt': {
      const interrupt = toInterrupt(data)

      if (!interrupt) {
        console.warn('[harness] dropped an interrupt frame with no usable interrupt', { event })

        return null
      }

      return { type: 'interrupt', interrupt }
    }

    case 'planning_failed':
      return {
        type: 'planning_failed',
        code: asString(data['code']) || 'planning_failed',
        reason: asString(data['reason']) || 'Planning could not finish.',
        hardFailures: asStringList(data['hard_failures']),
        recoveryAction: asString(data['recovery_action']),
        planId: asString(data['plan_id']),
      }

    case 'lifecycle': {
      const record = toSessionRecord(data)

      if (!record) {
        console.warn('[harness] dropped a lifecycle frame this client cannot read')

        return null
      }

      return { type: 'lifecycle', record }
    }

    case 'branch_ready': {
      const handoff = toBranchHandoff(data)

      if (!handoff) {
        console.warn('[harness] dropped a branch_ready frame with an incomplete handoff')

        return null
      }

      return { type: 'branch_ready', handoff }
    }

    // A frame without a readable target is dropped rather than folded:
    // the store writes what it names as the conversation's target, and half of one is wrong.
    case 'target_advanced': {
      const advanced = toTargetAdvanced(data)

      if (!advanced) {
        console.warn('[harness] dropped a target_advanced frame with no readable write target')

        return null
      }

      return { type: 'target_advanced', advanced }
    }

    case 'plan_adjusted': {
      const adjustment = toPlanAdjustment(data)

      if (!adjustment) {
        console.warn('[harness] dropped a plan_adjusted frame with no known level')

        return null
      }

      return { type: 'plan_adjusted', adjustment }
    }

    case 'error':
      return {
        type: 'error',
        message: asString(data['message']) || 'The harness reported an error.',
        retryable: asBoolean(data['retryable'], false),
        code: asString(data['code']),
      }

    case 'interrupted':
      return {
        type: 'interrupted',
        code: asString(data['code']),
        message: asString(data['message']) || 'The turn stopped before it finished.',
      }

    case 'usage':
      return {
        type: 'usage',
        estimatedTokens: asNumber(data['estimated_tokens'], 0),
        remainingToday: asNumber(data['remaining_today'], 0),
      }

    case 'end':
      return { type: 'end', paused: asBoolean(data['paused'], false) }

    default:
      console.warn('[harness] ignored an unknown frame', { event })

      return null
  }
}

function readToolFields(data: Record<string, unknown>): {
  toolUseId: string
  name: string
  label: string
} {
  return {
    toolUseId: asString(data['tool_use_id']),
    name: asString(data['name']),
    label: asString(data['label']),
  }
}

function parseJson(raw: string): Record<string, unknown> | null {
  try {
    const parsed: unknown = JSON.parse(raw)

    // The contract guarantees a JSON object, never a bare string — so anything else is a
    // frame we do not understand rather than one to coerce.
    return isRecord(parsed) ? parsed : null
  } catch {
    return null
  }
}
